import { useQuery } from '@apollo/client'
import { TitledPaper } from '@dai/components'
import { Check, SkipNext } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { logAnalyticsEvent } from 'api/Firebase'
import { uniqBy } from 'lodash'
import InviteUserForm from 'pages/forms/user/InviteUserForm'
import { useCallback, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import CenteredBox from '@/components/CenteredBox'
import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_TENANT_INVITATIONS,
  GetTenantInvitationsQuery,
  GetTenantInvitationsQueryVariables,
  InvitationFragment,
} from '@/graphql'
import useT from '@/i18n/useT'

import { AccountSetUpStepProps } from '../types'

const InviteListSkeleton = ({ count = 6 }: { count?: number }) => {
  return (
    <Box
      p={2}
      sx={{
        border: '1px solid lightGrey',
        borderRadius: 1,
        overflow: 'hidden',
        height: 400,
      }}
    >
      <Stack spacing={2}>
        {Array.from(new Array(count)).map((_, index) => (
          <Stack
            key={index}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              p: 2,
              borderRadius: 1,
            }}
          >
            <Skeleton variant="circular" width={24} height={24} />
            <Stack spacing={0.5} sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" height={24} />
              <Skeleton variant="text" width="50%" height={24} />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

type InviteUsersProps = AccountSetUpStepProps

const InviteUsers = (props: InviteUsersProps) => {
  const { t } = useT()
  return (
    <TitledPaper
      title={t('inviteUsers.title')}
      subtitle={t('inviteUsers.subtitle')}
    >
      <InviteUsersBody {...props} />
    </TitledPaper>
  )
}

const InviteUsersBody = ({ nextStep }: InviteUsersProps) => {
  const { t } = useT()
  const [inviteAnother, setInviteAnother] = useState(false)

  const invitation = useQuery<
    GetTenantInvitationsQuery,
    GetTenantInvitationsQueryVariables
  >(GET_TENANT_INVITATIONS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        limit: 20,
      },
    },
  })
  useRefresh(invitation.refetch)

  const onSuccess = (invite: InvitationFragment | null | undefined) => {
    if (invite) {
      invitation.updateQuery(prev => {
        return {
          ...prev,
          tenantInvitations: {
            ...prev.tenantInvitations,
            results: uniqBy([...prev.tenantInvitations.results, invite], 'id'),
          },
        }
      })
    }

    setInviteAnother(false)
  }

  const handleScroll = useCallback(() => {
    if (!invitation.data) return
    const currentLength = invitation.data.tenantInvitations.results.length
    const totalCount = invitation.data.tenantInvitations.totalCount

    if (currentLength < totalCount) {
      invitation.fetchMore({
        variables: {
          pagination: {
            offset: currentLength,
            limit: 20,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          return {
            ...fetchMoreResult,
            tenantInvitations: {
              ...fetchMoreResult.tenantInvitations,
              results: uniqBy(
                [
                  ...prev.tenantInvitations.results,
                  ...fetchMoreResult.tenantInvitations.results,
                ],
                'id',
              ),
            },
          }
        },
      })
    }
  }, [invitation])

  if (
    (inviteAnother || !invitation.data?.tenantInvitations.results.length) &&
    !invitation.loading
  ) {
    return (
      <InviteUserForm
        otherButtons={
          !invitation.data?.tenantInvitations.results.length
            ? [
                {
                  style: { flex: 1 },
                  variant: 'outlined',
                  color: 'secondary',
                  onClick: nextStep,
                  children: t('common.skipForNow'),
                  endIcon: <SkipNext />,
                },
              ]
            : undefined
        }
        onSuccess={invitation => {
          onSuccess(invitation)
          logAnalyticsEvent('set_up_invites_complete')
        }}
        onCancel={
          invitation.data?.tenantInvitations.results.length
            ? () => setInviteAnother(false)
            : undefined
        }
      />
    )
  }
  return (
    <Stack spacing={4}>
      {invitation.loading ? (
        <InviteListSkeleton />
      ) : (
        <InfiniteScroll
          next={handleScroll}
          hasMore={
            (invitation.data?.tenantInvitations.results.length || 0) <
            (invitation.data?.tenantInvitations.totalCount || 0)
          }
          loader={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 2,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          }
          dataLength={invitation.data?.tenantInvitations.results.length || 0}
          height={400}
          style={{
            border:
              invitation.data?.tenantInvitations.results.length &&
              '1px solid lightGrey',
            borderRadius: 8,
          }}
        >
          <Box p={2}>
            {invitation.error ? (
              <CenteredBox>
                <Alert severity="error">{invitation.error.message}</Alert>
              </CenteredBox>
            ) : (
              invitation.data?.tenantInvitations.results.map(invitation => {
                return (
                  <Stack
                    key={invitation.id}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Check />
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography variant="body1">
                        {invitation.email}
                      </Typography>
                      <Typography variant="body1">
                        {invitation.groups.map(group => group.name).join(', ')}
                      </Typography>
                    </Stack>
                  </Stack>
                )
              })
            )}
          </Box>
        </InfiniteScroll>
      )}

      <Stack direction={'row'} spacing={2}>
        <Button
          onClick={() => setInviteAnother(true)}
          fullWidth
          variant="outlined"
          disabled={invitation.loading}
        >
          {t('inviteUsers.inviteAnother')}
        </Button>
        <Button
          onClick={nextStep}
          fullWidth
          variant="contained"
          disabled={invitation.loading}
        >
          {t('common.continue')}
        </Button>
      </Stack>
    </Stack>
  )
}

export default InviteUsers
